/**
 * Manages slider initialization, state, and display updates
 */
export class SliderManager {
  // Default ranges for each slider
  static DEFAULTS = {
    TEMP_MIN: 0,
    TEMP_MAX: 2000,
    TEMP_MAX_INTERNAL: 6000, // Internal value when UI shows 2000+
    EXPANSION_MIN: 0,
    EXPANSION_MAX: 200,
    ABRASION_MIN: 0,
    ABRASION_MAX: 10000
  }

  constructor(tempSlider, expansionSlider, abrasionSlider, tempAmount, expAmount, abraAmount) {
    this.tempSlider = tempSlider
    this.expansionSlider = expansionSlider
    this.abrasionSlider = abrasionSlider
    this.tempAmount = tempAmount
    this.expAmount = expAmount
    this.abraAmount = abraAmount

    // Internal state
    this.sliderTempMin = SliderManager.DEFAULTS.TEMP_MIN
    this.sliderTempMax = SliderManager.DEFAULTS.TEMP_MAX
    this.sliderExpansionMin = SliderManager.DEFAULTS.EXPANSION_MIN
    this.sliderExpansionMax = SliderManager.DEFAULTS.EXPANSION_MAX
    this.sliderAbrasionMin = SliderManager.DEFAULTS.ABRASION_MIN
    this.sliderAbrasionMax = SliderManager.DEFAULTS.ABRASION_MAX

    this.initialize()
  }

  initialize() {
    // Set initial display values
    this.tempAmount.value = '0°F - 2000°F+'
    this.expAmount.value = '0% - 200%'
    this.abraAmount.value = '0 - 10k+ Cycles'

    // Initialize dual range sliders
    this.initializeDualRangeSlider(this.tempSlider)
    this.initializeDualRangeSlider(this.expansionSlider)
    this.initializeDualRangeSlider(this.abrasionSlider)
  }

  initializeDualRangeSlider(container) {
    const minInput = container.querySelector('.slider-min')
    const maxInput = container.querySelector('.slider-max')
    
    if (!minInput || !maxInput) return

    const min = parseInt(container.dataset.min)
    const max = parseInt(container.dataset.max)
    
    // Set initial values
    minInput.value = min
    maxInput.value = max
    
    // Start with sliders disabled (most permissive state)
    minInput.disabled = true
    maxInput.disabled = true

    // Ensure min doesn't exceed max and vice versa
    minInput.addEventListener('input', () => {
      const minVal = parseInt(minInput.value)
      const maxVal = parseInt(maxInput.value)
      if (minVal > maxVal) {
        minInput.value = maxVal
      }
    })

    maxInput.addEventListener('input', () => {
      const minVal = parseInt(minInput.value)
      const maxVal = parseInt(maxInput.value)
      if (maxVal < minVal) {
        maxInput.value = minVal
      }
    })
  }

  updateSliderDisplay(container) {
    const minInput = container.querySelector('.slider-min')
    const maxInput = container.querySelector('.slider-max')
    const min = parseInt(minInput.value)
    const max = parseInt(maxInput.value)

    // Update display values and internal state
    if (container === this.tempSlider) {
      this.sliderTempMin = min
      this.sliderTempMax = max === 2000 ? 6000 : max
      const display = max === 2000 
        ? `${min}°F - 2000°F+`
        : `${min}°F - ${max}°F`
      this.tempAmount.value = display
    } else if (container === this.expansionSlider) {
      this.sliderExpansionMin = min
      this.sliderExpansionMax = max
      this.expAmount.value = `${min}% - ${max}%`
    } else if (container === this.abrasionSlider) {
      this.sliderAbrasionMin = min
      this.sliderAbrasionMax = max
      const display = max === 10000
        ? `${min} - 10k+ Cycles`
        : `${min} - ${max} Cycles`
      this.abraAmount.value = display
    }
  }

  resetSlider(container, min, max) {
    const minInput = container.querySelector('.slider-min')
    const maxInput = container.querySelector('.slider-max')
    if (minInput) minInput.value = min
    if (maxInput) maxInput.value = max
    if (minInput) minInput.disabled = false
    if (maxInput) maxInput.disabled = false
  }

  resetAll() {
    this.resetSlider(this.tempSlider, 0, 2000)
    this.resetSlider(this.expansionSlider, 0, 200)
    this.resetSlider(this.abrasionSlider, 0, 10000)

    // Reset internal state
    this.sliderTempMin = 0
    this.sliderTempMax = 2000
    this.sliderExpansionMin = 0
    this.sliderExpansionMax = 200
    this.sliderAbrasionMin = 0
    this.sliderAbrasionMax = 10000

    this.tempAmount.value = '0°F - 2000°F+'
    this.expAmount.value = '0% - 200%'
    this.abraAmount.value = '0 - 10k+ Cycles'
  }

  updateAllDisplays() {
    // Temperature display
    const tempMax = this.sliderTempMax === 6000 ? 2000 : this.sliderTempMax
    const tempDisplay = this.sliderTempMax === 6000
      ? `${this.sliderTempMin}°F - 2000°F+`
      : `${this.sliderTempMin}°F - ${tempMax}°F`
    this.tempAmount.value = tempDisplay
    
    // Expansion display
    this.expAmount.value = `${this.sliderExpansionMin}% - ${this.sliderExpansionMax}%`
    
    // Abrasion display
    const abraDisplay = this.sliderAbrasionMax === 10000
      ? `${this.sliderAbrasionMin} - 10k+ Cycles`
      : `${this.sliderAbrasionMin} - ${this.sliderAbrasionMax} Cycles`
    this.abraAmount.value = abraDisplay
  }

  restoreValues(tempMin, tempMax, expMin, expMax, abraMin, abraMax) {
    if (tempMin !== undefined) this.sliderTempMin = tempMin
    if (tempMax !== undefined) this.sliderTempMax = tempMax
    if (expMin !== undefined) this.sliderExpansionMin = expMin
    if (expMax !== undefined) this.sliderExpansionMax = expMax
    if (abraMin !== undefined) this.sliderAbrasionMin = abraMin
    if (abraMax !== undefined) this.sliderAbrasionMax = abraMax

    // Update slider inputs
    if (tempMin !== undefined && tempMax !== undefined) {
      const minInput = this.tempSlider.querySelector('.slider-min')
      const maxInput = this.tempSlider.querySelector('.slider-max')
      if (minInput) minInput.value = tempMin
      if (maxInput) maxInput.value = tempMax === 6000 ? 2000 : tempMax
    }

    if (expMin !== undefined && expMax !== undefined) {
      const minInput = this.expansionSlider.querySelector('.slider-min')
      const maxInput = this.expansionSlider.querySelector('.slider-max')
      if (minInput) minInput.value = expMin
      if (maxInput) maxInput.value = expMax
    }

    if (abraMin !== undefined && abraMax !== undefined) {
      const minInput = this.abrasionSlider.querySelector('.slider-min')
      const maxInput = this.abrasionSlider.querySelector('.slider-max')
      if (minInput) minInput.value = abraMin
      if (maxInput) maxInput.value = abraMax
    }

    this.updateAllDisplays()
  }

  getState() {
    return {
      sliderTempMin: this.sliderTempMin,
      sliderTempMax: this.sliderTempMax,
      sliderExpansionMin: this.sliderExpansionMin,
      sliderExpansionMax: this.sliderExpansionMax,
      sliderAbrasionMin: this.sliderAbrasionMin,
      sliderAbrasionMax: this.sliderAbrasionMax
    }
  }

  /**
   * Get filter values for a specific slider type
   * Returns defaults if slider is disabled, current values if enabled
   * @param {string} sliderType - 'temperature', 'expansion', or 'abrasion'
   * @param {boolean} isEnabled - Whether the slider is currently enabled
   * @returns {Object} Object with min and max values
   */
  getFilterValues(sliderType, isEnabled) {
    if (!isEnabled) {
      // Return full range when disabled (most permissive)
      switch (sliderType) {
        case 'temperature':
          return {
            min: SliderManager.DEFAULTS.TEMP_MIN,
            max: SliderManager.DEFAULTS.TEMP_MAX_INTERNAL
          }
        case 'expansion':
          return {
            min: SliderManager.DEFAULTS.EXPANSION_MIN,
            max: SliderManager.DEFAULTS.EXPANSION_MAX
          }
        case 'abrasion':
          return {
            min: SliderManager.DEFAULTS.ABRASION_MIN,
            max: SliderManager.DEFAULTS.ABRASION_MAX
          }
      }
    }

    // Return current values when enabled, with fallback to defaults
    switch (sliderType) {
      case 'temperature':
        return {
          min: this.sliderTempMin ?? SliderManager.DEFAULTS.TEMP_MIN,
          max: this.sliderTempMax ?? SliderManager.DEFAULTS.TEMP_MAX_INTERNAL
        }
      case 'expansion':
        return {
          min: this.sliderExpansionMin ?? SliderManager.DEFAULTS.EXPANSION_MIN,
          max: this.sliderExpansionMax ?? SliderManager.DEFAULTS.EXPANSION_MAX
        }
      case 'abrasion':
        return {
          min: this.sliderAbrasionMin ?? SliderManager.DEFAULTS.ABRASION_MIN,
          max: this.sliderAbrasionMax ?? SliderManager.DEFAULTS.ABRASION_MAX
        }
    }
  }
}
